import { useState, useEffect } from 'react';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { userApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Search,
  Phone,
  Mail,
  MapPin,
  Clock,
  Activity
} from 'lucide-react';

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTeam = async () => {
    try {
      const res = await userApi.getAll();
      setTeam(res.data);
    } catch (err) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTeam, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const filteredTeam = team.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onlineCount = team.filter(m => m.status === 'online').length;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>Team</h1>
            <p className="text-sm text-gray-500 mt-1">
              {onlineCount} of {team.length} team members online
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <Activity className="w-3 h-3 mr-1" />
              {onlineCount} Online
            </Badge>
          </div>
        </div>

        {/* Search */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="team-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Team Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading team...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTeam.map((member) => (
              <Card
                key={member.id}
                className="border-gray-200 hover:border-blue-200 transition-all"
                data-testid={`team-member-${member.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-blue-600 text-white font-medium">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${
                          member.status === 'online' ? 'bg-green-500 pulse-online' : 'bg-gray-400'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-900 truncate">{member.name}</h3>
                        <Badge
                          variant="outline"
                          className={
                            member.role === 'admin'
                              ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : member.role === 'manager'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          }
                        >
                          {member.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {member.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{member.phone}</span>
                      </div>
                    )}
                    {member.current_location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>
                          {member.current_location.lat.toFixed(4)}, {member.current_location.lng.toFixed(4)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>
                        {member.status === 'online' ? 'Currently active' : 'Last seen: Unknown'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.location.href = `mailto:${member.email}`}
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Email
                    </Button>
                    {member.phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => window.location.href = `tel:${member.phone}`}
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && filteredTeam.length === 0 && (
          <Card className="border-gray-200">
            <CardContent className="text-center py-12">
              <p className="text-gray-500">No team members found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
